import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  findValidDescribingAid,
  GridRootUrl,
} from './context/hierarchy-context';

import LoaderOverlay from 'components/loader/loader-overlay';

import { ApiRoute } from 'enums';
import { fetchAndCache } from 'utils/fetch';
import { ArchivalAidMetadata, ArchivalUnitCopy, EadNode } from 'types';
import { getEadRoot } from './context/utils/ead-tree-context-utils';
import { CacheKeys } from 'cache-keys-enum';

const RedirectLegacyUrl = ({ variant }: { variant: 'aids' | 'funds' }) => {
  const ROOT_LEVEL = variant === 'aids' ? 0 : 1;
  const { replace } = useHistory();
  const { id, level, ead } = useParams<{
    id: string;
    level: string;
    ead: string;
  }>();

  if (variant === 'aids') {
    if (level === '0') {
      replace(`${GridRootUrl.AIDS}/${id}`);
    }
  } else if (variant === 'funds') {
    if (level === '0') {
      replace(`${GridRootUrl.COLLECTIONS}/${id}`);
    }
  }

  useEffect(() => {
    const decide = async () => {
      const levelInt = parseInt(level);
      if (isNaN(levelInt)) {
        replace('/not-found');
      }

      if (variant === 'funds' && levelInt === 1) {
        const aidDetail = await fetchAndCache<ArchivalAidMetadata>(
          `/api/${ApiRoute.ARCHIVAL_AID}/${id}`,
          [CacheKeys.ARCHIVAL_AID, id]
        );

        if (!aidDetail) {
          replace('/not-found');
          return;
        }
        replace(
          `${GridRootUrl.COLLECTIONS}/${aidDetail.archivalCollection?.id}/pomucka/${aidDetail.id}`
        );
      }

      if (levelInt > ROOT_LEVEL && ead === 'ead') {
        ///////////////////////////////////////////////////
        //////////////// REDIRECT NESTED EAD //////////////
        ///////////////////////////////////////////////////
        const eadNode = await fetchAndCache<EadNode>(
          '/api/' + ApiRoute.ARCHIVAL_AID + `/ead/${id}`,
          [CacheKeys.EAD, id]
        );

        if (!eadNode) {
          replace('/not-found');
          return;
        }

        const root = getEadRoot(eadNode);

        if (!root) {
          replace('/not-found');
          return;
        }

        if (variant === 'aids') {
          replace(`${GridRootUrl.AIDS}/${root.rootArchivalAidId}/ead/${id}`);
        } else if (variant === 'funds') {
          replace(
            `${GridRootUrl.COLLECTIONS}/${root.rootArchivalCollectionId}/ead/${id}`
          );
        }
      } else if (levelInt > ROOT_LEVEL) {
        ///////////////////////////////////////////////////
        ////////////// REDIRECT ARCHIVAL UNIT /////////////
        ///////////////////////////////////////////////////
        const archival = await fetchAndCache<ArchivalUnitCopy>(
          '/api/' + ApiRoute.ARCHIVAL_UNIT + `/${id}`,
          [CacheKeys.ARCHIVAL_UNIT, id]
        );
        if (!archival) {
          replace('/not-found');
          return;
        }

        const aidId = findValidDescribingAid(
          archival.archivalUnitReference.describingArchivalAids
        )?.id;
        if (!aidId) {
          replace('/not-found');
          return;
        }

        if (variant === 'aids') {
          replace(`${GridRootUrl.AIDS}/${aidId}/archivalie/${id}`);
        } else if (variant === 'funds') {
          const collectionId = findValidDescribingAid(
            archival.archivalUnitReference.describingArchivalAids
          )?.archivalCollection?.id;

          if (!collectionId) {
            replace('/not-found');
            return;
          }
          replace(
            `${GridRootUrl.COLLECTIONS}/${collectionId}/archivalie/${id}`
          );
        }
      }
    };
    decide();
  }, [ROOT_LEVEL, ead, id, level, replace, variant]);

  return <LoaderOverlay />;
};

export default RedirectLegacyUrl;
